import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3010/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const onLoginPage = window.location.pathname === "/login";

    if (error.response?.status === 401 && !onLoginPage) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }

    return Promise.reject(error);
  },
);

export const authAPI = {
  login: (identifier, password) =>
    api.post("/auth/login", { identifier, password }),
  verify: () => api.get("/auth/verify"),
};

export const platformAPI = {
  listShops: () => api.get("/platform/shops"),
  getShop: (id) => api.get(`/platform/shops/${id}`),
  createShop: (data) => api.post("/platform/shops", data),
  suspendShop: (id, reason) =>
    api.post(`/platform/shops/${id}/suspend`, { reason }),
  reactivateShop: (id, currentPeriodEnd) =>
    api.post(`/platform/shops/${id}/reactivate`, currentPeriodEnd ? { current_period_end: currentPeriodEnd } : {}),
  updateModules: (id, enabled_modules) =>
    api.put(`/platform/shops/${id}/modules`, { enabled_modules }),
  assignPlan: (id, data) => api.post(`/platform/shops/${id}/assign-plan`, data),
  overrideSubscription: (id, data) => api.put(`/platform/shops/${id}/subscription`, data),
  listPayments: (id) => api.get(`/platform/shops/${id}/payments`),
  recordPayment: (id, data) => api.post(`/platform/shops/${id}/payments`, data),
  paymentDocument: (id, paymentId) =>
    api.get(`/platform/shops/${id}/payments/${paymentId}/document`, { responseType: "text", transformResponse: (r) => r }),
  getAuditLog: (id) => api.get(`/platform/shops/${id}/audit-log`),
  // A pack: which shop's subscription pays for this one (null: it pays for itself)
  setPack: (id, billingShopId) => api.put(`/platform/shops/${id}/pack`, { billing_shop_id: billingShopId }),
  listDevices: (id) => api.get(`/platform/shops/${id}/devices`),
  revokeDevice: (id, deviceId) => api.delete(`/platform/shops/${id}/devices/${deviceId}`),
  scheduleDeletion: (id, body) => api.post(`/platform/shops/${id}/deletion`, body),
  cancelDeletion: (id) => api.delete(`/platform/shops/${id}/deletion`),
  listReferrals: (status = "qualified") => api.get("/platform/referrals", { params: { status } }),
  referralCount: () => api.get("/platform/referrals/count"),
  approveReferral: (id, note) => api.post(`/platform/referrals/${id}/approve`, { note }),
  rejectReferral: (id, note) => api.post(`/platform/referrals/${id}/reject`, { note }),

  listSignups: (status = "pending") =>
    api.get("/platform/signups", { params: { status } }),
  signupCount: () => api.get("/platform/signups/count"),
  shopData: (id) => api.get(`/platform/shops/${id}/data`),
  previewShopData: (id, groups) => api.post(`/platform/shops/${id}/data/preview`, { groups }),
  clearShopData: (id, body) => api.post(`/platform/shops/${id}/data/clear`, body),
  sendOwnerVerification: (id) => api.post(`/platform/shops/${id}/send-verification`),
  approveShop: (id, note) => api.post(`/platform/shops/${id}/approve`, { note }),
  rejectShop: (id, message) => api.post(`/platform/shops/${id}/reject`, { message }),
  listProblemReports: (status = "open") => api.get("/platform/problem-reports", { params: { status } }),
  problemReportCount: () => api.get("/platform/problem-reports/count"),
  handleProblemReport: (id, note) => api.post(`/platform/problem-reports/${id}/handle`, { note }),
  listFlags: () => api.get("/platform/flags"),
  updateFlag: (key, data) => api.put(`/platform/flags/${key}`, data),
  setFlagOverride: (key, shopId, value) => api.put(`/platform/flags/${key}/overrides/${shopId}`, { value }),
  setRing: (shopId, ring) => api.put(`/platform/shops/${shopId}/ring`, { ring }),
  getSettings: () => api.get("/platform/settings"),
  updateSettings: (data) => api.put("/platform/settings", data),

  listPlans: () => api.get("/platform/subscription-plans"),
  createPlan: (data) => api.post("/platform/subscription-plans", data),
  updatePlan: (id, data) => api.put(`/platform/subscription-plans/${id}`, data),

  analyticsOverview: () => api.get("/platform/analytics/overview"),
  activationFunnel: ({ weeks = 12, source = "all" } = {}) =>
    api.get("/platform/analytics/activation", { params: { weeks, source } }),
  stuckShops: () => api.get("/platform/analytics/stuck"),
  shopScorecard: (id, days = 30) =>
    api.get(`/platform/analytics/shops/${id}`, { params: { days } }),

  listTemplates: (status) =>
    api.get("/platform/product-templates", { params: status ? { status } : {} }),
  getTemplate: (id) => api.get(`/platform/product-templates/${id}`),
  createTemplate: (name) => api.post("/platform/product-templates", { name }),
  renameTemplate: (id, name) => api.put(`/platform/product-templates/${id}`, { name }),
  approveTemplate: (id) => api.post(`/platform/product-templates/${id}/approve`),
  templateValidation: (id) => api.get(`/platform/product-templates/${id}/validation`),
  listCatalogRequests: (status = "open") =>
    api.get("/platform/catalog-requests", { params: { status } }),
  handleCatalogRequest: (id, note) => api.post(`/platform/catalog-requests/${id}/handle`, { note }),
  archiveTemplate: (id) => api.post(`/platform/product-templates/${id}/archive`),
  templateUsage: (id) => api.get(`/platform/product-templates/${id}/usage`),
  templateDeleteImpact: (id) => api.get(`/platform/product-templates/${id}/delete-impact`),
  deleteTemplate: (id) => api.delete(`/platform/product-templates/${id}`),
  copyTemplate: (id, shop_id, item_ids) =>
    api.post(`/platform/product-templates/${id}/copy`, { shop_id, item_ids }),

  // The pool of real shop products a template can be built from (house
  // recipes excluded server-side).
  browseShopProducts: ({ search, category, shop_id, exclude_template_id } = {}) =>
    api.get("/platform/shop-products", {
      params: { search, category, shop_id, exclude_template_id },
    }),
  addTemplateItemFromProduct: (templateId, sourceProductId) =>
    api.post(`/platform/product-templates/${templateId}/items`, {
      source_product_id: sourceProductId,
    }),
  // "Add all" / "Add selected" from the browser: one transactional request.
  addTemplateItemsFromProducts: (templateId, sourceProductIds) =>
    api.post(`/platform/product-templates/${templateId}/items/bulk`, {
      source_product_ids: sourceProductIds,
    }),
  // "Remove" on a selection: one statement on the server, so all or none
  removeTemplateItems: (templateId, itemIds) =>
    api.post(`/platform/product-templates/${templateId}/items/bulk-delete`, {
      item_ids: itemIds,
    }),
  // `data` is a FormData (name/category/brand/subcategory/description/
  // juice_details/images) built by TemplateProductForm — never JSON, since
  // it may carry image files.
  addTemplateItem: (templateId, data) =>
    api.post(`/platform/product-templates/${templateId}/items`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  updateTemplateItem: (templateId, itemId, data) =>
    api.put(`/platform/product-templates/${templateId}/items/${itemId}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  removeTemplateItem: (templateId, itemId) =>
    api.delete(`/platform/product-templates/${templateId}/items/${itemId}`),
  // Small JSON edit of one product (label, moving in/out of a range).
  patchTemplateItem: (templateId, itemId, data) =>
    api.put(`/platform/product-templates/${templateId}/items/${itemId}`, data),

  // Ranges (variant groups). `data` is a FormData, like the item form's.
  createTemplateGroup: (templateId, data) =>
    api.post(`/platform/product-templates/${templateId}/groups`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  updateTemplateGroup: (templateId, groupId, data) =>
    api.put(`/platform/product-templates/${templateId}/groups/${groupId}`, data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  deleteTemplateGroup: (templateId, groupId) =>
    api.delete(`/platform/product-templates/${templateId}/groups/${groupId}`),
  addTemplateGroupMembers: (templateId, groupId, labels) =>
    api.post(`/platform/product-templates/${templateId}/groups/${groupId}/members`, { labels }),
  applyTemplateGroup: (templateId, groupId, fields) =>
    api.post(`/platform/product-templates/${templateId}/groups/${groupId}/apply`, { fields }),
};

export default api;
